import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Alert,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import { CloudUpload as UploadIcon, Image as ImageIcon } from '@mui/icons-material';
import ImageHandler from '../../../../utils/ImageHandler';
import { FlowComponent } from '../../../../flow-core/FlowComponent';
import { ImageCore } from '../../../../flow-core/components/ImageCore';

/**
 * The width of a Flow's content column, in dp: a 360dp device less 16dp of padding each side.
 *
 * `width` and `height` on an Image are absolute dp in that column, and every preview we draw is
 * NARROWER than a phone, 244px inside the live phone frame, more on the canvas, less in a flow
 * thumbnail. So a dp value cannot be drawn as a pixel value; what has to survive is its
 * PROPORTION to the column, which is what `aspect-ratio` against this width gives, at any
 * preview size and without any of the three knowing about the others.
 *
 * Checked against Meta's own preview on 2026-09-22: a `height: 200` image measured 160px in a
 * ~265px-wide panel (0.60); 200/328 is 0.61.
 */
const CONTENT_WIDTH_DP = 328;

/**
 * Device pixels per dp. Phones render a flow at two or three; below two, upscaling is visible.
 * The warning fires at MIN so it does not nag, and names SHARP as what to aim for.
 */
const MIN_DENSITY = 2;
const SHARP_DENSITY = 3;

/**
 * The size, in dp, an image will actually be drawn at.
 *
 * <p>Not the same as its box: `contain` fits the picture inside and `cover` fills it, so the
 * same source in the same box is drawn at two different sizes, and it is the drawn size that
 * decides whether the resolution is enough.
 */
export function drawnSize(properties, natural) {
  if (!natural || !natural.width || !natural.height) {
    return null;
  }
  const boxWidth = Number(properties.width) > 0 ? Number(properties.width) : CONTENT_WIDTH_DP;
  const boxHeight = Number(properties.height) > 0
    ? Number(properties.height)
    : (boxWidth * natural.height) / natural.width;
  const fit = properties['scale-type'] === 'cover' ? Math.max : Math.min;
  const scale = fit(boxWidth / natural.width, boxHeight / natural.height);
  return { width: natural.width * scale, height: natural.height * scale };
}

/**
 * Why this picture will look soft, in its own numbers, or null when it will not.
 *
 * <p>The blur that started this was a 32x32 favicon drawn at 80dp: no layout property could
 * have fixed it, and nothing in the product said so. The asset store now refuses anything that
 * small outright; this catches the other half, where the image is big enough to upload and
 * still too small for where it has been put.
 */
export function resolutionWarning(properties, natural) {
  const drawn = drawnSize(properties, natural);
  if (!drawn) {
    return null;
  }
  const needed = Math.round(Math.max(drawn.width, drawn.height) * MIN_DENSITY);
  const longest = Math.max(natural.width, natural.height);
  if (longest >= needed) {
    return null;
  }
  const sharp = Math.round(Math.max(drawn.width, drawn.height) * SHARP_DENSITY);
  return `This image is ${natural.width}x${natural.height}, and here it is drawn about `
    + `${Math.round(drawn.width)} points across. Phones draw a flow at two to three pixels per `
    + `point, so it will look soft, around ${sharp} pixels on its longest side would be sharp.`;
}

// Image Property Editor Component - defined outside to avoid recreation on every render
const ImagePropertyEditor = ({ properties, onChange }) => {
  const [imageUpload, setImageUpload] = useState({
    loading: false,
    error: null,
    validating: false,
    progress: 0,
    fileName: null,
  });
  // Read off the rendered preview rather than decoded here: the browser has already parsed the
  // image to draw it, and naturalWidth is the same number a decoder would hand back.
  const [natural, setNatural] = useState(null);

  const handleImageFileUpload = async (file) => {
    setImageUpload({
      loading: true,
      error: null,
      validating: false,
      progress: 0,
      fileName: file.name,
    });

    try {
      ImageHandler.validateFile(file);
      setImageUpload((prev) => ({ ...prev, progress: 30 }));

      const rawBase64 = await ImageHandler.convertFile(file);
      setImageUpload((prev) => ({ ...prev, progress: 80 }));

      onChange('src', rawBase64);
      setImageUpload((prev) => ({ ...prev, progress: 100 }));

      setTimeout(() => {
        setImageUpload({
          loading: false,
          error: null,
          validating: false,
          progress: 0,
          fileName: null,
        });
      }, 1000);
    } catch (error) {
      setImageUpload({
        loading: false,
        error: error.message,
        validating: false,
        progress: 0,
        fileName: null,
      });
    }
  };

  const handleImageUrlChange = async (url) => {
    try {
      const whatsappFormat = await ImageHandler.toWhatsAppFormat(url);
      onChange('src', whatsappFormat);
      setImageUpload({ loading: false, error: null, validating: false });
    } catch (error) {
      onChange('src', url);
      setImageUpload({
        loading: false,
        error: `URL validation failed: ${error.message}`,
        validating: false,
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Image Preview */}
      {properties.src && (
        <Paper sx={{ p: 1, textAlign: 'center' }}>
          <img
            src={ImageHandler.toDisplayFormat(properties.src)}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '120px',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
            onLoad={(e) => setNatural({
              width: e.target.naturalWidth,
              height: e.target.naturalHeight,
            })}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            {natural ? `${natural.width}x${natural.height}, ` : ''}
            {ImageHandler.getFormatDescription(properties.src)}
          </Typography>
        </Paper>
      )}

      {resolutionWarning(properties, natural) && (
        <Alert severity="warning">{resolutionWarning(properties, natural)}</Alert>
      )}

      {/* URL Input */}
      <TextField
        fullWidth
        label="Image URL"
        value={properties.src || ''}
        onChange={(e) => handleImageUrlChange(e.target.value)}
        required
        size="small"
        error={!!imageUpload.error}
        helperText={
          imageUpload.error ||
          'Enter image URL or upload file below. JPEG or PNG; anything larger than WhatsApp '
          + 'allows is resized for you, but at least 200 pixels on its longest side.'
        }
        InputProps={{
          endAdornment: imageUpload.validating && <CircularProgress size={20} />,
        }}
      />

      {/* File Upload */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={imageUpload.loading ? <CircularProgress size={16} /> : <UploadIcon />}
            disabled={imageUpload.loading}
            size="small"
          >
            {imageUpload.loading ? 'Uploading...' : 'Upload Image'}
            <input
              type="file"
              hidden
              accept="image/jpeg,image/jpg,image/png"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleImageFileUpload(file);
                }
              }}
            />
          </Button>
          <Typography variant="caption" color="text.secondary">
            JPEG or PNG, at least 200px
          </Typography>
        </Box>

        {imageUpload.loading && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {imageUpload.fileName
                ? `Converting ${imageUpload.fileName}...`
                : 'Processing image...'}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={imageUpload.progress}
              sx={{ height: 4, borderRadius: 2 }}
            />
          </Box>
        )}
      </Box>

      {imageUpload.error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {imageUpload.error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Alt Text"
        value={properties['alt-text'] || ''}
        onChange={(e) => onChange('alt-text', e.target.value)}
        size="small"
      />

      <FormControl fullWidth size="small">
        <InputLabel>Scale Type</InputLabel>
        <Select
          value={properties['scale-type'] || 'contain'}
          onChange={(e) => onChange('scale-type', e.target.value)}
          label="Scale Type"
        >
          <MenuItem value="cover">Cover</MenuItem>
          <MenuItem value="contain">Contain</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label="Width"
          type="number"
          value={properties.width || ''}
          onChange={(e) => onChange('width', e.target.value ? Number(e.target.value) : undefined)}
          size="small"
          sx={{ flex: 1 }}
        />
        <TextField
          label="Height"
          type="number"
          value={properties.height || ''}
          onChange={(e) => onChange('height', e.target.value ? Number(e.target.value) : undefined)}
          size="small"
          sx={{ flex: 1 }}
        />
      </Box>
    </Box>
  );
};

export const ImageComponent = new FlowComponent({
  ...ImageCore,
  renderPreview(properties) {
    const hasImage = !!properties.src;
    // 'contain' is the default in ImageCore and in transformFromWhatsApp, so it is the default
    // here too. This used to fall back to 'cover', which previewed a cropped wordmark for a
    // component the compiler would render whole.
    const objectFit = properties['scale-type'] === 'cover' ? 'cover' : 'contain';
    // WhatsApp lays the image out in a box of exactly this many dp and then fits or crops the
    // picture inside it. The preview ignored both and drew every image full-width up to 240px,
    // which is why an 80dp logo came out three times its size and a 200dp `cover` strip came
    // out uncropped, the two differences against Meta's preview on 2026-09-22.
    const height = Number(properties.height) > 0 ? Number(properties.height) : null;
    const width = Number(properties.width) > 0 ? Number(properties.width) : null;
    const box = height
      ? { aspectRatio: `${width || CONTENT_WIDTH_DP} / ${height}`, height: 'auto' }
      // No height: WhatsApp gives the picture its own shape, so the preview does too. The cap
      // is a preview safeguard in pixels, not a dp value, a percentage max-height would
      // resolve against the container's HEIGHT, which is not the axis this is about.
      : { maxHeight: 240, height: 'auto' };

    return (
      <Box sx={{ mb: 1 }}>
        {hasImage ? (
          <Box
            component="img"
            src={ImageHandler.toDisplayFormat(properties.src)}
            alt={properties['alt-text'] || 'Image'}
            sx={{
              display: 'block',
              // A picture narrower than the screen sits in the middle, as on the phone.
              mx: 'auto',
              width: width ? `${Math.min(100, (width / CONTENT_WIDTH_DP) * 100)}%` : '100%',
              ...box,
              objectFit,
              borderRadius: 2,
              backgroundColor: '#f5f5f5',
            }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 4,
              px: 2,
              color: 'text.secondary',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: '#fafafa',
            }}
          >
            <ImageIcon sx={{ fontSize: 32, opacity: 0.6 }} />
            <Typography variant="body2">Click to upload or drop an image</Typography>
            <Typography variant="caption">JPEG or PNG, at least 200px on its longest side</Typography>
          </Box>
        )}
        {/* Alt-text is NOT drawn. WhatsApp never shows it: it is what a screen reader
            announces and what appears if the image fails, so printing it under the picture
            made the preview show a caption the customer will never see. It stays on the img
            element above, where it belongs. */}
      </Box>
    );
  },
  renderPropertyEditor(properties, onChange) {
    return <ImagePropertyEditor properties={properties} onChange={onChange} />;
  },
});
